import type { GetStaticProps, NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import type { SSRConfig } from 'next-i18next';
import { useTranslation } from 'next-i18next';
import get from 'lodash/get';
import { AxiosResponseHeaders } from 'axios';
import { requestWithCache } from '../core/request';
import ContentWrapper from '../components/common/ContentWrapper';
import markdownComponents from '../components/common/markdownComponents';
import type FooterType from '../../types/Footer';
import { reportError } from '../core/errorReporting/errorReporting';
import translator from '../core/helpers/translator';
import { Locale } from '../core/constants';
import Markdown from '../components/common/Markdown/Markdown';
import { generateLinks } from '../core/helpers/link';
import PageLayout from '../components/common/PageLayout';
import HotelCount from '../../types/HotelCount';

type TermsOfService = Partial<{
  title: string;
  content: string;
  updated_at: string;
}>;

type Props = {
  title: string;
  content: string;
  updated_at: string;
  locale: string;
  footer: FooterType | undefined;
  hotelCount: HotelCount | number | undefined;
} & Metadata &
  SSRConfig;

const TermsOfServicePage: NextPage<Props> = ({
  locale,
  title,
  content,
  updated_at,
  footer,
  hotelCount,
}) => {
  const { t } = useTranslation();
  return (
    <PageLayout
      footer={{
        content: footer,
      }}
      hotelCount={hotelCount}
    >
      <main className="grow bg-gray-50">
        <ContentWrapper className="bg-white pb-20 pt-12 sm:pb-36 sm:pt-16">
          <h1 id="terms-of-service-title" className="mb-4 text-title3 font-bold">
            {title}
          </h1>
          <p className="mb-4 text-small" data-testid="terms-of-service-date">
            {t('page:terms_of_service.last_updated')}
            {new Date(updated_at).toLocaleString(locale === 'ja' ? 'ja-JP' : 'en-US', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </p>
          <Markdown components={markdownComponents}>{content}</Markdown>
          <div className="mt-12 flex justify-end sm:mt-16">
            <a href="#terms-of-service-title">
              <p className="border-b border-orange-700 text-small text-orange-700">
                {t('common:back_to_top')}
              </p>
            </a>
          </div>
        </ContentWrapper>
      </main>
    </PageLayout>
  );
};

const revalidate = process.env.REVALIDATE_TERMS_PAGES
  ? Number(process.env.REVALIDATE_TERMS_PAGES)
  : false;

export const getStaticProps: GetStaticProps<
  Props,
  {
    hotel: string;
  }
> = async ({ locale }) => {
  const ssrConfig = await serverSideTranslations(translator.locale.get(locale), [
    'common',
    'component',
    'page',
  ]);

  let termsAndConditions: TermsOfService;
  try {
    termsAndConditions = await requestWithCache<TermsOfService>({
      url: `${
        process.env.NEXT_PUBLIC_BFF_URL
      }/terms-and-conditions/terms-of-service?locale=${translator.locale.get(locale)}&tabist=ok`,
      method: 'GET',
    });
  } catch (error) {
    reportError(error as Error);
    throw new Error(
      `terms-of-service Page, request "/terms-and-conditions/terms-of-service?locale=${translator.locale.get(
        locale
      )}" failed`
    );
  }

  let footer: FooterType;
  try {
    footer = await requestWithCache<FooterType>({
      url: `${process.env.NEXT_PUBLIC_BFF_URL}/footer?locale=${translator.locale.get(
        locale
      )}&tabist=ok`,
      method: 'GET',
    });
  } catch (error) {
    reportError(error as Error);
    throw new Error(
      `terms-of-service Page, request "/footer?locale=${translator.locale.get(locale)}" failed`
    );
  }

  const hotelCount = await requestWithCache<AxiosResponseHeaders & { 'x-total-count': number }>({
    url: `${process.env.NEXT_PUBLIC_BFF_URL}/hotels?locale=${Locale.JAPANESE}&tabist=ok`,
    method: 'HEAD',
  });

  const namespaces =
    // eslint-disable-next-line no-underscore-dangle
    locale && ssrConfig._nextI18Next.initialI18nStore[translator.locale.get(locale)];
  const url = new URL(`${process.env.NEXT_PUBLIC_SITE_URL}/terms-of-service`);

  const translationTargets = {
    title: get(namespaces.page, 'terms_of_service.title', null),
    description: get(namespaces.page, 'meta.description', null),
  };

  const translatedMetadata = await translator.batch.translate(translationTargets, locale);

  const metadata = {
    title: translatedMetadata.title,
    description: translatedMetadata.description,
    'og:locale': locale,
    'og:url': `${url.origin}${locale !== Locale.JAPANESE ? `/${locale}` : ''}${url.pathname}`,
  };

  return {
    props: {
      ...ssrConfig,
      title: termsAndConditions.title || '',
      content: termsAndConditions.content || '',
      updated_at: termsAndConditions.updated_at || '',
      locale: locale || Locale.JAPANESE,
      footer,
      metadata: locale && namespaces && metadata,
      links: generateLinks(url),
      hotelCount: hotelCount['x-total-count'] && Number(hotelCount['x-total-count']),
    },
    revalidate,
  };
};

export default TermsOfServicePage;
